/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
        this.initSizeGroupFilters();
        this.initColorGroupFilters();
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      if (evt.target.matches('.js-size-group')) {
        if (evt.type === 'change') this.handleSizeGroupChange(evt.target);
        return;
      }

      if (evt.target.matches('.js-color-group')) {
        if (evt.type === 'change') this.handleColorGroupChange(evt.target);
        return;
      }

      if (evt.target.matches('.js-filter-option-search')) {
        this.filterOptionList(evt.target);
        return;
      }

      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Filters a long facet option list without submitting the facets form.
     * @param {HTMLInputElement} input - Search field inside the facet.
     */
    filterOptionList(input) {
      const filter = input.closest('.filter');
      const query = input.value.trim().toLocaleLowerCase('de');
      let visibleCount = 0;

      filter.querySelectorAll('.filter__size-options > li').forEach((item) => {
        const label = item.querySelector('.filter__label .flex-auto')?.textContent.trim().toLocaleLowerCase('de') || '';
        const matches = label.includes(query);
        item.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      const emptyMessage = filter.querySelector('.filter-option-search__empty');
      if (emptyMessage) emptyMessage.hidden = visibleCount > 0;
    }

    /**
     * Replaces the long list of exact frame sizes with broad, approximate groups.
     * The original Shopify checkboxes stay in the form so native filtering keeps working.
     */
    initSizeGroupFilters() {
      this.querySelectorAll('.filter--size').forEach((filter) => {
        if (filter.querySelector('.size-groups')) return;

        const sourceList = filter.querySelector('.filter__size-options');
        if (!sourceList) return;

        const groups = this.getSizeGroups();
        const sourceOptions = [...sourceList.querySelectorAll('li')].map((item) => {
          const input = item.querySelector('input[type="checkbox"]');
          const label = item.querySelector('.filter__label .flex-auto')?.textContent.trim() || '';
          return { input, label, group: this.getSizeGroupKey(label) };
        }).filter((option) => option.input && option.group);

        if (!sourceOptions.length) return;

        const groupList = document.createElement('ul');
        groupList.className = 'size-groups';
        groupList.setAttribute('role', 'list');

        groups.forEach((group) => {
          const options = sourceOptions.filter((option) => option.group === group.key);
          if (!options.length) return;

          const availableOptions = options.filter((option) => !option.input.disabled || option.input.checked);
          const activeOptions = options.filter((option) => option.input.checked);
          const item = document.createElement('li');
          const id = `${filter.id}-${group.key}`;

          item.className = 'size-group';
          item.innerHTML = `
            <input type="checkbox" class="visually-hidden focus-label-before js-size-group" id="${id}" data-size-group="${group.key}">
            <label for="${id}" class="size-group__label">
              <span class="size-group__name">${group.label}</span>
              <span class="size-group__range">${group.range}</span>
            </label>`;

          const groupInput = item.querySelector('.js-size-group');
          groupInput.checked = activeOptions.length > 0;
          groupInput.indeterminate = activeOptions.length > 0 && activeOptions.length < availableOptions.length;
          groupInput.disabled = availableOptions.length === 0;
          groupList.appendChild(item);
        });

        const note = document.createElement('p');
        note.className = 'size-groups__note';
        note.textContent = 'Ungefähre Zuordnung – Hersteller und Fahrradtypen können abweichen.';

        sourceList.before(note, groupList);
        filter.classList.add('filter--size-grouped');
        this.renderActiveSizeGroups(filter, sourceOptions, groups);
      });
    }

    /**
     * Returns the broad size groups shown to customers.
     * @returns {Array}
     */
    getSizeGroups() {
      return [
        { key: 'xxs', label: 'XXS', range: 'ca. 44–47 cm' },
        { key: 'xs', label: 'XS', range: 'ca. 48–50 cm' },
        { key: 's', label: 'S', range: 'ca. 51–53 cm' },
        { key: 'm', label: 'M', range: 'ca. 54–56 cm · 17″' },
        { key: 'l', label: 'L', range: 'ca. 57–59 cm · 19–20″' },
        { key: 'xl', label: 'XL', range: 'ca. 60–62 cm · 21″' },
        { key: 'xxl', label: 'XXL', range: 'ca. 63–64 cm' },
        { key: 'special', label: 'Sondergrößen', range: 'UNI · Unisex · S-Sizing' }
      ];
    }

    /**
     * Maps one exact Shopify size value to an approximate customer-facing group.
     * Explicit letter sizes take precedence over numeric values in mixed labels.
     * @param {string} label
     * @returns {string|null}
     */
    getSizeGroupKey(label) {
      const value = label.toUpperCase().replace(/\s+/g, ' ').trim();

      if (/^(UNI|UNISEX)$/.test(value) || /^S\d+$/.test(value)) return 'special';
      if (value.includes('XXL')) return 'xxl';
      if (value.includes('XXS')) return 'xxs';
      if (value.includes('XL')) return 'xl';
      if (value.includes('XS')) return 'xs';
      if (value === 'S' || value === 'S/M' || /\/ S$/.test(value)) return 's';
      if (value === 'M' || value === 'M/L' || value === 'ML' || /\/ M$/.test(value)) return 'm';
      if (value === 'L' || /^L \/ /.test(value) || /\/ L$/.test(value)) return 'l';

      const centimeters = Number.parseInt(value.match(/^\d+/)?.[0], 10);
      if (Number.isFinite(centimeters)) {
        if (centimeters > 30) {
          if (centimeters <= 47) return 'xxs';
          if (centimeters <= 50) return 'xs';
          if (centimeters <= 53) return 's';
          if (centimeters <= 56) return 'm';
          if (centimeters <= 59) return 'l';
          if (centimeters <= 62) return 'xl';
          return 'xxl';
        }

        if (value.includes('17"')) return 'm';
        if (value.includes('19"') || value.includes('20"')) return 'l';
        if (value.includes('21"')) return 'xl';
      }

      return null;
    }

    /**
     * Selects or clears all exact Shopify values assigned to one broad group.
     * @param {HTMLInputElement} groupInput
     */
    handleSizeGroupChange(groupInput) {
      const filter = groupInput.closest('.filter--size');
      const sourceInputs = [...filter.querySelectorAll('.filter__size-options input[type="checkbox"]')]
        .filter((input) => {
          const label = input.closest('li')?.querySelector('.filter__label .flex-auto')?.textContent.trim() || '';
          return this.getSizeGroupKey(label) === groupInput.dataset.sizeGroup && (!input.disabled || input.checked);
        });

      sourceInputs.forEach((input) => {
        input.checked = groupInput.checked;
      });

      groupInput.indeterminate = false;
      if (sourceInputs[0]) sourceInputs[0].dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * Collapses the many native active-size chips into one removable chip per group.
     * @param {HTMLElement} filter
     * @param {Array} sourceOptions
     * @param {Array} groups
     */
    renderActiveSizeGroups(filter, sourceOptions, groups) {
      const activeOptions = sourceOptions.filter((option) => option.input.checked);
      if (!activeOptions.length || !this.activeFiltersList) return;

      const activeLabels = new Set(activeOptions.map((option) => option.label));
      this.activeFiltersList.querySelectorAll('a.active-filter').forEach((link) => {
        if (activeLabels.has(link.textContent.trim())) link.remove();
      });

      groups.forEach((group) => {
        const groupOptions = activeOptions.filter((option) => option.group === group.key);
        if (!groupOptions.length) return;

        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'active-filter flex items-center text-current size-group-chip';
        chip.innerHTML = '<span aria-hidden="true" class="size-group-chip__icon">×</span>'
          + `<span>${group.label}</span>`;
        chip.addEventListener('click', () => {
          groupOptions.forEach((option) => { option.input.checked = false; });
          groupOptions[0].input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        this.activeFiltersList.prepend(chip);
      });
    }

    /**
     * Replaces exact color names with broad color families.
     */
    initColorGroupFilters() {
      this.querySelectorAll('.filter').forEach((filter) => {
        const filterLabel = filter.querySelector('summary .font-bold')?.textContent.trim().toLocaleLowerCase('de') || '';
        if (!['farbe', 'color', 'colour'].includes(filterLabel) || filter.querySelector('.color-groups')) return;

        const sourceList = filter.querySelector('.filter__content > ul');
        if (!sourceList) return;

        const groups = this.getColorGroups();
        const sourceOptions = [...sourceList.querySelectorAll(':scope > li')].map((item) => {
          const input = item.querySelector('input[type="checkbox"]');
          const label = item.querySelector('.filter__label .flex-auto')?.textContent.trim() || '';
          return { input, label, group: this.getColorGroupKey(label) };
        }).filter((option) => option.input);

        if (!sourceOptions.length) return;

        const groupList = document.createElement('ul');
        groupList.className = 'color-groups';
        groupList.setAttribute('role', 'list');

        groups.forEach((group) => {
          const options = sourceOptions.filter((option) => option.group === group.key);
          if (!options.length) return;

          const availableOptions = options.filter((option) => !option.input.disabled || option.input.checked);
          const activeOptions = options.filter((option) => option.input.checked);
          const item = document.createElement('li');
          const id = `${filter.id}-color-${group.key}`;

          item.className = 'color-group';
          item.innerHTML = `
            <input type="checkbox" class="visually-hidden focus-label-before js-color-group" id="${id}" data-color-group="${group.key}">
            <label for="${id}" class="color-group__label">
              <span class="color-group__swatch color-group__swatch--${group.key}" aria-hidden="true"></span>
              <span class="color-group__name">${group.label}</span>
            </label>`;

          const groupInput = item.querySelector('.js-color-group');
          groupInput.checked = activeOptions.length > 0;
          groupInput.indeterminate = activeOptions.length > 0 && activeOptions.length < availableOptions.length;
          groupInput.disabled = availableOptions.length === 0;
          groupList.appendChild(item);
        });

        sourceList.before(groupList);
        filter.classList.add('filter--color-grouped');
        this.renderActiveColorGroups(sourceOptions, groups);
      });
    }

    /**
     * Returns broad color families and their customer-facing labels.
     * @returns {Array}
     */
    getColorGroups() {
      return [
        { key: 'black', label: 'Schwarz' },
        { key: 'white', label: 'Weiß / Creme' },
        { key: 'gray', label: 'Grau / Silber' },
        { key: 'blue', label: 'Blau' },
        { key: 'red', label: 'Rot / Rosa' },
        { key: 'green', label: 'Grün' },
        { key: 'yellow', label: 'Gelb / Orange' },
        { key: 'brown', label: 'Braun / Beige' },
        { key: 'purple', label: 'Lila' },
        { key: 'other', label: 'Sonstige' }
      ];
    }

    /**
     * Maps one exact Shopify color value to a broad family.
     * @param {string} label
     * @returns {string}
     */
    getColorGroupKey(label) {
      const value = label.toLocaleLowerCase('de').trim();

      if (value.includes('schwarz')) return 'black';
      if (value.includes('weiß') || value.includes('weiss') || value.includes('creme')) return 'white';
      if (value.includes('grau') || value.includes('silber')) return 'gray';
      if (value.includes('blau') || value.includes('celeste') || value.includes('türkis') || value.includes('tuerkis')) return 'blue';
      if (value.includes('rot') || value.includes('rosa') || value.includes('bordeaux') || value.includes('bordeuax')) return 'red';
      if (value.includes('grün') || value.includes('gruen') || value.includes('verde')) return 'green';
      if (value.includes('gelb') || value.includes('orange')) return 'yellow';
      if (value.includes('braun') || value.includes('beige') || value.includes('sand') || value.includes('gold')) return 'brown';
      if (value.includes('lila') || value.includes('violett') || value.includes('flieder')) return 'purple';
      return 'other';
    }

    /**
     * Selects or clears all exact Shopify values assigned to one color family.
     * @param {HTMLInputElement} groupInput
     */
    handleColorGroupChange(groupInput) {
      const filter = groupInput.closest('.filter--color-grouped');
      const sourceInputs = [...filter.querySelectorAll('.filter__content > ul:not(.color-groups) input[type="checkbox"]')]
        .filter((input) => {
          const label = input.closest('li')?.querySelector('.filter__label .flex-auto')?.textContent.trim() || '';
          return this.getColorGroupKey(label) === groupInput.dataset.colorGroup && (!input.disabled || input.checked);
        });

      sourceInputs.forEach((input) => {
        input.checked = groupInput.checked;
      });

      groupInput.indeterminate = false;
      if (sourceInputs[0]) sourceInputs[0].dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * Collapses native active-color chips into one removable chip per family.
     * @param {Array} sourceOptions
     * @param {Array} groups
     */
    renderActiveColorGroups(sourceOptions, groups) {
      const activeOptions = sourceOptions.filter((option) => option.input.checked);
      if (!activeOptions.length || !this.activeFiltersList) return;

      const activeLabels = new Set(activeOptions.map((option) => option.label));
      this.activeFiltersList.querySelectorAll('a.active-filter').forEach((link) => {
        if (activeLabels.has(link.textContent.trim())) link.remove();
      });

      groups.forEach((group) => {
        const groupOptions = activeOptions.filter((option) => option.group === group.key);
        if (!groupOptions.length) return;

        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'active-filter flex items-center text-current size-group-chip';
        chip.innerHTML = '<span aria-hidden="true" class="size-group-chip__icon">×</span>'
          + `<span>${group.label}</span>`;
        chip.addEventListener('click', () => {
          groupOptions.forEach((option) => { option.input.checked = false; });
          groupOptions[0].input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        this.activeFiltersList.prepend(chip);
      });
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
